import { EventEmitter } from 'eventemitter3';
import Mark from '@patsnap/mark.js';
import { defaultMarkOpt, exactMarkOpt, postFormatKeywordsConfigs } from './core';
import { differenceWith, get, isEqual, isFunction, isUndefined, reduce } from 'lodash';
import { CACHE_KEY_HIGHLIGHT_CONFIG, CACHE_KEY_HIGHLIGHT_ENABLE, HIGHLIGHT_CHANNEL_IDENTITY, HIGHLIGHT_DEFAULT_KEYWORDS, TAG_NAME } from './contants';
import { Store } from './utils';
import { ChannelHighlightConfig } from './channel';
const DATA_HIGHLIGHT_KEYWORD = 'data-highlight-keyword';
const DATA_HIGHLIGHT_NAMESPACE = 'data-highlight-namespace';
function getSelectorByKeywords(keywordOnAttr) {
    return `[${DATA_HIGHLIGHT_KEYWORD}="${encodeURI(keywordOnAttr)}"]`;
}
;
export class HighlightInstance extends EventEmitter {
    /**
     *
     * @param namespace 命名空间
     */
    constructor(namespace) {
        super();
        this.name = 'HighlightInstance';
        this.config = [];
        this.parser = new DOMParser();
        this.cache = Store();
        this._cache_ = {};
        this.unsub = this.sub(this.createStyles);
        this._namespace = namespace || `${Date.now()}`;
    }
    static getInstance(namespace) {
        if (!HighlightInstance.instances[namespace]) {
            const instance = new HighlightInstance(namespace);
            HighlightInstance.instances[namespace] = instance;
        }
        return HighlightInstance.instances[namespace];
    }
    dispose() {
        this.unsub();
        this.clearStyles();
        if (this.styleNode) {
            document.head.appendChild(this.styleNode);
            this.styleNode = undefined;
        }
        if (HighlightInstance.instances[this.namespace]) {
            delete HighlightInstance.instances[this.namespace];
        }
    }
    get namespace() {
        return this._namespace;
    }
    tpl({ color, keywordOnAttr }) {
        return `${TAG_NAME}[${DATA_HIGHLIGHT_NAMESPACE}="${this.namespace}"]${getSelectorByKeywords(keywordOnAttr)}{
            background-color:${color};
            color: black;
        }
        `;
    }
    /**
     * 高亮 html 文本
     * @param html html 文本
     * @param options
     * @returns
     */
    markText(html, options) {
        const config = this.config.filter((conf) => conf.keyword);
        if (config.length <= 0)
            return html;
        const parsedHtml = this.parser.parseFromString(`<div>${html}</div>`, 'text/html');
        const dom = parsedHtml.body.children[0];
        const instance = new Mark(dom);
        config.forEach(({ keyword, keywordOnAttr, needExactMatch } /* , index */) => {
            const each = (element) => {
                element.setAttribute(DATA_HIGHLIGHT_KEYWORD, encodeURI(keywordOnAttr));
                element.setAttribute(DATA_HIGHLIGHT_NAMESPACE, this.namespace);
            };
            const opts = { ...(needExactMatch ? exactMarkOpt : defaultMarkOpt), each };
            instance.mark(keyword, { ...opts, ...options });
        });
        const result = dom.innerHTML;
        return result;
    }
    /**
     * 推送高亮配置
     * @param value
     * @returns
     */
    pub(value) {
        const data = postFormatKeywordsConfigs(value);
        /* 未发生修改时不推送 */
        if (isEqual(data, this.config)) {
            return;
        }
        const old = this.config;
        this.config = data;
        this.emit('change', this.config, old);
    }
    /**
     * 订阅高亮配置更新
     * @param listener
     * @returns
     */
    sub(listener) {
        this.on('change', listener);
        return () => {
            this.off('change', listener);
        };
    }
    /**
     * 获取当前高亮配置的拷贝
     * @returns
     */
    getConfigCopy() {
        return this.config.map(record => {
            return { ...record };
        });
    }
    enable(bool) {
        const key = `${this.namespace}:${CACHE_KEY_HIGHLIGHT_ENABLE}`;
        if (!isUndefined(bool)) {
            this.cache.set(key, { data: !!bool, id: HIGHLIGHT_CHANNEL_IDENTITY });
            if (!bool) {
                this.clearStyles();
            }
            else {
                this.createStyles();
            }
        }
        return this.cache.get(key) ? get(this.cache.get(key), 'data') : true;
    }
    clearStyles() {
        if (this.styleNode) {
            this.styleNode.innerHTML = '';
        }
    }
    createStyles() {
        if (!this.enable()) {
            this.clearStyles();
            return;
        }
        if (!this.styleNode) {
            this.styleNode = document.createElement('style');
            this.styleNode.type = 'text/css';
            document.head.appendChild(this.styleNode);
        }
        const config = this.getConfigCopy();
        if (config.length === 0) {
            this.styleNode.innerHTML = '';
            return;
        }
        const content = reduce(config, (style, { color, keywordOnAttr }) => style + this.tpl({ color, keywordOnAttr }), `
            .patsnap-mark-current-focus {
                background-color: #ab8500 !important;
            }
            `);
        if (content !== this.styleNode.innerHTML) {
            this.styleNode.innerHTML = content;
        }
    }
    createMixin() {
        const self = this;
        return {
            data() {
                return {
                    highlightMixin_count: -1,
                };
            },
            created() {
                //@ts-ignore
                this.unsub = self.sub(() => {
                    //@ts-ignore
                    this.highlightMixin_count += 1;
                });
            },
            destroyed() {
                //@ts-ignore
                this.unsub();
            },
            methods: {
                //@ts-ignore
                highlightMixin_hlText(value) {
                    //@ts-ignore
                    return self.markText(value, this.highlightMixin_count);
                },
            },
        };
    }
    createChannel({ origin }) {
        const { namespace, _cache_ } = this;
        const _id_ = `${namespace}-${origin}`;
        if (!_cache_[_id_]) {
            const cache = Store();
            const key = `${namespace}:${CACHE_KEY_HIGHLIGHT_CONFIG}`;
            const keyOfEnable = `analytics:${namespace}:${CACHE_KEY_HIGHLIGHT_ENABLE}`;
            const listeners = {};
            let count = 0;
            _cache_[_id_] = {
                on(fn) {
                    if (!isFunction(fn))
                        return;
                    const index = count++;
                    listeners[index] = fn;
                    window.onstorage = (event) => {
                        if (event.oldValue === event.newValue) {
                            return;
                        }
                        const statusChanged = event.key === keyOfEnable;
                        if (event.key === `analytics:${key}`
                            || statusChanged) {
                            const { id } = JSON.parse(event.newValue || '');
                            if (id === origin) {
                                return;
                            }
                            let value;
                            if (statusChanged) {
                                // 禁用/启动 状态变化时，取本地已存储的数据
                                value = cache.get(key);
                            }
                            else {
                                // 配置变化时，取变化的值
                                value = JSON.parse(event.newValue || '');
                            }
                            const { data } = value || {};
                            const config = new ChannelHighlightConfig(data, { namespace });
                            Object.keys(listeners).forEach((key) => {
                                const enable = get(JSON.parse(event.newValue || ''), 'data');
                                const state = statusChanged ? { enable } : undefined;
                                listeners[key](config, state, event);
                            });
                        }
                    };
                    return () => {
                        delete listeners[index];
                    };
                },
                emit(data) {
                    const { data: oldData = HIGHLIGHT_DEFAULT_KEYWORDS } = cache.get(key) || {};
                    const diff = differenceWith(data, oldData, isEqual);
                    if (!diff) {
                        return;
                    }
                    cache.set(key, { id: origin, data });
                }
            };
        }
        return _cache_[_id_];
    }
}
HighlightInstance.instances = {};
//# sourceMappingURL=klass.js.map